/**
 * Google Places Image Service
 * Google Maps Platform Places API (New) を使用した画像取得
 * 
 * 実装フロー:
 * 1. Text Search/Nearby Search でPlace IDを取得
 * 2. Place Details で photos[].name を取得
 * 3. Place Photos API で画像を取得
 * 4. 適切な帰属表示を行う
 */

import { ImageData, Location } from '../types/core';

export interface PlaceSearchResult {
    placeId: string;
    displayName: string;
    photos?: PlacePhoto[];
    location?: Location;
}

export interface PlacePhoto {
    name: string; // places/PLACE_ID/photos/PHOTO_ID
    widthPx: number;
    heightPx: number;
    authorAttributions: AuthorAttribution[];
}

export interface AuthorAttribution {
    displayName: string;
    uri?: string;
    photoUri?: string;
}

export interface PlaceImageOptions {
    maxWidthPx?: number;
    maxHeightPx?: number;
    skipHttpsUrlCheck?: boolean;
}

/**
 * Google Places APIを使用してスポットを検索
 */
export async function searchPlaceByNameAndLocation(
    name: string,
    location: Location,
    radius: number = 100
): Promise<PlaceSearchResult | null> {
    try {
        // APIエンドポイント（Cloudflare Workers経由）
        const searchParams = new URLSearchParams({
            name: name,
            lat: location.lat.toString(),
            lng: location.lng.toString(),
            radius: radius.toString()
        });

        const response = await fetch(`/api/places/search?${searchParams}`);

        if (!response.ok) {
            console.warn('Places search failed:', response.status);
            return null;
        }

        const result = await response.json() as any;
        return result.place || null;

    } catch (error) {
        console.error('Places search error:', error);
        return null;
    }
}

/**
 * Place IDから詳細情報（写真含む）を取得
 */
export async function getPlaceDetails(placeId: string): Promise<PlaceSearchResult | null> {
    try {
        const response = await fetch(`/api/places/details?placeId=${placeId}`);

        if (!response.ok) {
            console.warn('Place details failed:', response.status);
            return null;
        }

        const result = await response.json() as any;
        return result.place || null;

    } catch (error) {
        console.error('Place details error:', error);
        return null;
    }
}

/**
 * Google Places Photo APIを使用して画像URLを取得
 */
export async function getPlacePhotoUrl(
    photoName: string,
    options: PlaceImageOptions = {}
): Promise<string | null> {
    try {
        const {
            maxWidthPx = 640,
            maxHeightPx = 400,
            skipHttpsUrlCheck = false
        } = options;

        const params = new URLSearchParams({
            photoName: photoName,
            maxWidthPx: maxWidthPx.toString(),
            maxHeightPx: maxHeightPx.toString(),
            skipHttpsUrlCheck: skipHttpsUrlCheck.toString()
        });

        // Cloudflare Workers経由で画像を取得
        const photoUrl = `/api/places/photo?${params}`;

        // 画像の存在確認
        const testResponse = await fetch(photoUrl, { method: 'HEAD' });
        if (!testResponse.ok) {
            return null;
        }

        return photoUrl;

    } catch (error) {
        console.error('Place photo error:', error);
        return null;
    }
}

/**
 * スポット情報から最適な画像を取得
 */
export async function getSpotImageFromPlaces(
    spotName: string,
    location: Location,
    fallbackSources: string[] = []
): Promise<ImageData> {
    // 1. 既存のデータソース画像を優先
    for (const source of fallbackSources) {
        if (source && source.startsWith('http')) {
            return {
                url: source,
                source: 'provider',
                attribution: null
            };
        }
    }

    try {
        // 2. Google Places検索
        const place = await searchPlaceByNameAndLocation(spotName, location);

        if (!place || !place.photos || place.photos.length === 0) {
            // 写真が見つからない場合はプレースホルダー
            return {
                url: null,
                source: 'placeholder',
                attribution: null
            };
        }

        // 3. 最初の写真を取得
        const firstPhoto = place.photos[0];
        const photoUrl = await getPlacePhotoUrl(firstPhoto.name, {
            maxWidthPx: 640,
            maxHeightPx: 400
        });

        if (!photoUrl) {
            return {
                url: null,
                source: 'placeholder',
                attribution: null
            };
        }

        // 4. 帰属表示を生成
        const attribution = generatePhotoAttribution(firstPhoto.authorAttributions);

        return {
            url: photoUrl,
            source: 'google_places',
            attribution: attribution
        };

    } catch (error) {
        console.error('Google Places image fetch error:', error);
        return {
            url: null,
            source: 'placeholder',
            attribution: null
        };
    }
}

/**
 * 写真の帰属表示を生成
 */
export function generatePhotoAttribution(attributions: AuthorAttribution[]): string {
    if (!attributions || attributions.length === 0) {
        return '© Google';
    }

    // 最初の帰属情報を使用
    const firstAttribution = attributions[0];

    if (firstAttribution.displayName) {
        return `© ${firstAttribution.displayName}`;
    }

    return '© Google';
}

/**
 * 画像キャッシュ管理
 */
class PlaceImageCache {
    private cache = new Map<string, Promise<ImageData>>();
    private readonly maxAge = 4 * 60 * 60 * 1000; // 4時間（Googleポリシー準拠）

    /**
     * キャッシュキーを生成
     */
    private generateKey(spotName: string, location: Location): string {
        // 位置情報を100m単位で丸める（プライバシー保護）
        const roundedLat = Math.round(location.lat * 1000) / 1000;
        const roundedLng = Math.round(location.lng * 1000) / 1000;
        return `${spotName}:${roundedLat}:${roundedLng}`;
    }

    /**
     * キャッシュから取得
     */
    async get(spotName: string, location: Location): Promise<ImageData | null> {
        const key = this.generateKey(spotName, location);
        const cached = this.cache.get(key);

        if (cached) {
            try {
                return await cached;
            } catch {
                // キャッシュエラーの場合は削除
                this.cache.delete(key);
                return null;
            }
        }

        return null;
    }

    /**
     * キャッシュに保存
     */
    set(spotName: string, location: Location, imagePromise: Promise<ImageData>): void {
        const key = this.generateKey(spotName, location);
        this.cache.set(key, imagePromise);

        // 自動削除タイマー
        setTimeout(() => {
            this.cache.delete(key);
        }, this.maxAge);
    }

    /**
     * キャッシュをクリア
     */
    clear(): void {
        this.cache.clear();
    }

    /**
     * キャッシュサイズを取得
     */
    size(): number {
        return this.cache.size;
    }
}

// シングルトンキャッシュインスタンス
export const placeImageCache = new PlaceImageCache();

/**
 * キャッシュ付きの画像取得
 */
export async function getCachedSpotImage(
    spotName: string,
    location: Location,
    fallbackSources: string[] = []
): Promise<ImageData> {
    // キャッシュから取得を試行
    const cached = await placeImageCache.get(spotName, location);
    if (cached) {
        return cached;
    }

    // 新規取得
    const imagePromise = getSpotImageFromPlaces(spotName, location, fallbackSources);
    placeImageCache.set(spotName, location, imagePromise);

    return imagePromise;
}

/**
 * 複数スポットの画像を並列取得
 */
export async function getBatchSpotImages(
    spots: Array<{ name: string; location: Location; fallbackSources?: string[] }>
): Promise<ImageData[]> {
    const promises = spots.map(spot =>
        getCachedSpotImage(spot.name, spot.location, spot.fallbackSources || [])
    );

    return Promise.all(promises);
}

export default {
    searchPlaceByNameAndLocation,
    getPlaceDetails,
    getPlacePhotoUrl,
    getSpotImageFromPlaces,
    getCachedSpotImage,
    getBatchSpotImages,
    generatePhotoAttribution,
    placeImageCache
};